import React, { useState } from 'react';
import { MixingWork, InspectionBreakdown } from '@twinsketch/topika-model';

interface SolidContentAccuracyChartProps {
  mixingWorks: MixingWork[];
  inspectionBreakdown: InspectionBreakdown;
}

const SolidContentAccuracyChart: React.FC<SolidContentAccuracyChartProps> = ({
  mixingWorks,
  inspectionBreakdown,
}) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const { slurrySolidContent, slurrySolidContentTolerance, slurrySolidContentNote } =
    inspectionBreakdown;

  // Filter out mixing works without solid content data
  const validMixingWorks = (mixingWorks || []).filter(
    w => w.measuredSolidContentPercent != null && w.calculatedSolidContentPercent != null
  );

  if (validMixingWorks.length === 0) {
    return (
      <div
        style={{
          width: '100%',
          maxWidth: '48rem',
          margin: '0 auto',
          padding: '1.5rem',
          backgroundColor: 'white',
          borderRadius: '0.5rem',
          border: '1px solid #e5e7eb',
        }}
      >
        <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>
          Solid Content Accuracy
        </h3>
        <p style={{ color: '#6b7280', fontStyle: 'italic' }}>No solid content data available</p>
      </div>
    );
  }

  // Calculate target range if available
  const hasTargetRange = slurrySolidContent != null && slurrySolidContentTolerance != null;
  const targetMin = hasTargetRange ? slurrySolidContent! - slurrySolidContentTolerance! : null;
  const targetMax = hasTargetRange ? slurrySolidContent! + slurrySolidContentTolerance! : null;

  // Calculate chart dimensions and scale
  const allValues = validMixingWorks.flatMap(w => [
    w.measuredSolidContentPercent,
    w.calculatedSolidContentPercent,
  ]);

  if (hasTargetRange) {
    allValues.push(targetMin!, targetMax!);
  }

  const minValue = Math.floor(Math.min(...allValues) - 1);
  const maxValue = Math.ceil(Math.max(...allValues) + 1);

  // Dynamic label configuration based on data density
  const dataCount = validMixingWorks.length;
  const shouldRotate = dataCount > 5;

  const chartHeight = 320;
  const minChartWidth = 400;
  const barWidthIdeal = 16;
  const gapBetweenBars = 4;

  // Each group has 2 bars (measured + calculated)
  const groupWidth = barWidthIdeal * 2 + gapBetweenBars * 3;
  const totalGroupsWidth = dataCount * groupWidth;
  const chartWidth = Math.max(minChartWidth, totalGroupsWidth + 200);

  // Dynamic bottom padding based on rotation
  const bottomPadding = shouldRotate ? 100 : 60;
  const padding = {
    top: 40,
    right: 140,
    bottom: bottomPadding,
    left: 70,
  };

  const plotHeight = chartHeight - padding.top - padding.bottom;
  const plotWidth = chartWidth - padding.left - padding.right;

  // Scale function
  const scaleY = (value: number) => {
    const range = maxValue - minValue;
    return plotHeight - ((value - minValue) / range) * plotHeight;
  };

  // Bar configuration
  const groupSpacing = plotWidth / validMixingWorks.length;
  const barWidth = Math.min(barWidthIdeal, groupSpacing / 3);
  const innerGap = barWidth * 0.2;

  // Y-axis ticks
  const yTicks: number[] = [];
  const tickInterval = maxValue - minValue <= 5 ? 0.5 : 1;
  for (let i = minValue; i <= maxValue; i += tickInterval) {
    yTicks.push(i);
  }

  // Check if value is within spec
  const isInSpec = (value: number): boolean => {
    if (!hasTargetRange) return true;
    return value >= targetMin! && value <= targetMax!;
  };

  // Calculate delta from target
  const getDeltaFromTarget = (measured: number): number | null => {
    if (!slurrySolidContent) return null;
    return measured - slurrySolidContent;
  };

  return (
    <div className="chart-card">
      <div>
        <h3>Solid Content Accuracy</h3>
        <span className="chart-helper-text">Hover over bars for details</span>
      </div>

      {slurrySolidContentNote && (
        <p
          style={{
            fontSize: '0.75rem',
            color: '#6b7280',
            marginBottom: '1rem',
            fontStyle: 'italic',
          }}
        >
          {slurrySolidContentNote}
        </p>
      )}
      <div className="chart-container">
        <svg width={chartWidth} height={chartHeight}>
          {/* Legend at top right - positioned above the plot area */}
          <g>
            {/* Legend background */}
            <rect
              x={chartWidth - padding.right + 10}
              y={5}
              width={130}
              height={50}
              fill="white"
              stroke="#e5e7eb"
              strokeWidth="1"
              rx="4"
              opacity="0.95"
            />

            {/* Measured - Blue */}
            <rect
              x={chartWidth - padding.right + 18}
              y={13}
              width={12}
              height={12}
              fill="#3b82f6"
              opacity="0.8"
              rx="2"
            />
            <text
              x={chartWidth - padding.right + 36}
              y={19}
              fontSize="11"
              fill="#374151"
              dominantBaseline="middle"
            >
              Measured
            </text>

            {/* Calculated - Gray */}
            <rect
              x={chartWidth - padding.right + 18}
              y={33}
              width={12}
              height={12}
              fill="#6b7280"
              opacity="0.6"
              rx="2"
            />
            <text
              x={chartWidth - padding.right + 36}
              y={39}
              fontSize="11"
              fill="#374151"
              dominantBaseline="middle"
            >
              Calculated
            </text>
          </g>

          {/* Target Range Band */}
          {hasTargetRange && (
            <>
              <rect
                x={padding.left}
                y={scaleY(targetMax!) + padding.top}
                width={plotWidth}
                height={scaleY(targetMin!) - scaleY(targetMax!)}
                fill="#dcfce7"
                stroke="#22c55e"
                strokeWidth="2"
                strokeDasharray="4,4"
                opacity="0.4"
              />

              {/* Target line */}
              <line
                x1={padding.left}
                y1={scaleY(slurrySolidContent!) + padding.top}
                x2={padding.left + plotWidth}
                y2={scaleY(slurrySolidContent!) + padding.top}
                stroke="#16a34a"
                strokeWidth="2"
                strokeDasharray="6,3"
              />

              {/* Target label */}
              <text
                x={padding.left + plotWidth + 5}
                y={scaleY(slurrySolidContent!) + padding.top}
                fontSize="10"
                fill="#15803d"
                dominantBaseline="middle"
              >
                Target ({slurrySolidContent}%)
              </text>
            </>
          )}

          {/* Y-axis */}
          <line
            x1={padding.left}
            y1={padding.top}
            x2={padding.left}
            y2={chartHeight - padding.bottom}
            stroke="#374151"
            strokeWidth="2"
          />

          {/* Y-axis ticks and labels */}
          {yTicks.map(tick => (
            <g key={tick}>
              <line
                x1={padding.left - 5}
                y1={scaleY(tick) + padding.top}
                x2={padding.left}
                y2={scaleY(tick) + padding.top}
                stroke="#374151"
                strokeWidth="2"
              />
              <text
                x={padding.left - 10}
                y={scaleY(tick) + padding.top}
                textAnchor="end"
                dominantBaseline="middle"
                fontSize="12"
                fill="#374151"
              >
                {tick.toFixed(1)}
              </text>
            </g>
          ))}

          {/* Y-axis label */}
          <text
            x={20}
            y={padding.top + plotHeight / 2}
            textAnchor="middle"
            fontSize="14"
            fill="#374151"
            transform={`rotate(-90, 20, ${padding.top + plotHeight / 2})`}
          >
            Solid Content (%)
          </text>

          {/* X-axis */}
          <line
            x1={padding.left}
            y1={chartHeight - padding.bottom}
            x2={chartWidth - padding.right}
            y2={chartHeight - padding.bottom}
            stroke="#374151"
            strokeWidth="2"
          />

          {/* Bars and labels */}
          {validMixingWorks.map((work, index) => {
            const groupCenter = padding.left + index * groupSpacing + groupSpacing / 2;

            // Measured bar (blue)
            const measuredX = groupCenter - barWidth - innerGap / 2;
            const measuredHeight = plotHeight - scaleY(work.measuredSolidContentPercent);
            const measuredY = scaleY(work.measuredSolidContentPercent) + padding.top;
            const measuredInSpec = isInSpec(work.measuredSolidContentPercent);

            // Calculated bar (gray)
            const calculatedX = groupCenter + innerGap / 2;
            const calculatedHeight = plotHeight - scaleY(work.calculatedSolidContentPercent);
            const calculatedY = scaleY(work.calculatedSolidContentPercent) + padding.top;

            const deltaFromTarget = getDeltaFromTarget(work.measuredSolidContentPercent);

            // Tooltip dimensions
            const tooltipWidth = 200;
            const tooltipHeight = 110;
            const tooltipPadding = 12;

            // Calculate tooltip position
            let tooltipX = groupCenter - tooltipWidth / 2;
            if (tooltipX < padding.left) {
              tooltipX = padding.left + 5;
            } else if (tooltipX + tooltipWidth > chartWidth - padding.right) {
              tooltipX = chartWidth - padding.right - tooltipWidth - 5;
            }

            const tooltipY = Math.min(measuredY, calculatedY) - tooltipHeight - 15;

            return (
              <g key={work.lotNumber}>
                {/* Measured bar */}
                <rect
                  x={measuredX}
                  y={measuredY}
                  width={barWidth}
                  height={measuredHeight}
                  fill="#3b82f6"
                  opacity={hoveredIndex === index ? 1 : 0.8}
                  style={{ cursor: 'pointer', transition: 'opacity 0.2s' }}
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                />

                {/* Calculated bar */}
                <rect
                  x={calculatedX}
                  y={calculatedY}
                  width={barWidth}
                  height={calculatedHeight}
                  fill="#6b7280"
                  opacity={hoveredIndex === index ? 0.8 : 0.6}
                  style={{ cursor: 'pointer', transition: 'opacity 0.2s' }}
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                />

                {/* Tooltip on hover */}
                {hoveredIndex === index && (
                  <>
                    {/* Tooltip background with shadow */}
                    <rect
                      x={tooltipX}
                      y={tooltipY}
                      width={tooltipWidth}
                      height={tooltipHeight}
                      fill="white"
                      stroke="#d1d5db"
                      strokeWidth="1.5"
                      rx="6"
                      style={{
                        pointerEvents: 'none',
                        filter: 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1))',
                      }}
                    />

                    {/* Tooltip content */}
                    <text
                      x={tooltipX + tooltipPadding}
                      y={tooltipY + 22}
                      textAnchor="start"
                      fontSize="12"
                      fill="#374151"
                      fontWeight="700"
                      style={{ pointerEvents: 'none' }}
                    >
                      {work.lotNumber}
                    </text>

                    <text
                      x={tooltipX + tooltipPadding}
                      y={tooltipY + 40}
                      textAnchor="start"
                      fontSize="11"
                      fill="#6b7280"
                      style={{ pointerEvents: 'none' }}
                    >
                      Date:{' '}
                      {new Date(work.date).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </text>

                    <text
                      x={tooltipX + tooltipPadding}
                      y={tooltipY + 58}
                      textAnchor="start"
                      fontSize="11"
                      fill="#1e40af"
                      fontWeight="600"
                      style={{ pointerEvents: 'none' }}
                    >
                      📊 Measured: {work.measuredSolidContentPercent.toFixed(2)}%
                    </text>

                    <text
                      x={tooltipX + tooltipPadding}
                      y={tooltipY + 75}
                      textAnchor="start"
                      fontSize="11"
                      fill="#4b5563"
                      fontWeight="600"
                      style={{ pointerEvents: 'none' }}
                    >
                      📐 Calculated: {work.calculatedSolidContentPercent.toFixed(2)}%
                    </text>

                    <text
                      x={tooltipX + tooltipPadding}
                      y={tooltipY + 93}
                      textAnchor="start"
                      fontSize="10"
                      fill={measuredInSpec ? '#059669' : '#dc2626'}
                      fontWeight="600"
                      style={{ pointerEvents: 'none' }}
                    >
                      {deltaFromTarget !== null
                        ? `✓ Δ from target: ${deltaFromTarget > 0 ? '+' : ''}${deltaFromTarget.toFixed(2)}% ${measuredInSpec ? '(In Spec)' : '(Out of Spec)'}`
                        : `✓ Difference: ${(work.measuredSolidContentPercent - work.calculatedSolidContentPercent).toFixed(2)}%`}
                    </text>
                  </>
                )}
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
};

export default SolidContentAccuracyChart;
